// Live per-word vocab sync — complements cloudSync.js (which only backs up
// the WHOLE localStorage state, pull-only-on-empty-device). This one merges
// individual "word" cards bidirectionally on every grade, so a second device
// (or the future review hub) can push a grade and have it show up here too.
// Reuses the same sync code as cloudSync.js — one code per learner.
// Mirrors taigi-video's public/review.js queueSync()/syncNow() pattern.
import { ensureSyncCode, getSyncCode } from './cloudSync';
import { vocabStore, VOCAB_KEY } from '../staticStore';

const FN = '/.netlify/functions/vocabSync';

let syncing = false, syncPending = false, syncTimer = null;

// Several components call useVocab() independently — each holds its own
// React state loaded once from localStorage. A background merge writes
// localStorage directly, so every instance subscribes here to know to
// reload from it.
const listeners = new Set();
export function onVocabChange(fn) { listeners.add(fn); return () => listeners.delete(fn); }
function notify(cards) { listeners.forEach((fn) => { try { fn(cards); } catch (_) {} }); }

async function syncNow() {
  const code = getSyncCode();
  if (!code) return;
  if (syncing) { syncPending = true; return; }
  syncing = true;
  try {
    const r = await fetch(FN, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ code, snapshot: { cards: vocabStore.getAll() } }),
    });
    if (!r.ok) throw new Error('http ' + r.status);
    const data = await r.json();
    if (data && data.cards) {
      const before = JSON.stringify(vocabStore.getAll());
      const after = JSON.stringify(data.cards);
      if (before !== after) {
        vocabStore.replaceKeys(data.cards);
        notify(data.cards);
      }
    }
  } catch (_) {
    // offline — the next grade (or the interval below) retries
  } finally {
    syncing = false;
    if (syncPending) { syncPending = false; queueVocabSync(); }
  }
}

// Debounced push: a grade fires this repeatedly during a review session, but
// the request waits for a short lull so it's one call, not one per card.
export function queueVocabSync() {
  if (!getSyncCode()) return;   // no code adopted yet — cloudSync.js's own
  clearTimeout(syncTimer);      // push will create one on the first real save
  syncTimer = setTimeout(syncNow, 2500);
}

// Called once at boot (App.js) so a fresh device without local words still
// pulls in cards pushed from elsewhere (another device, or the hub).
export function startVocabSync() {
  if (localStorage.getItem(VOCAB_KEY)) ensureSyncCode();   // rides along if a code already exists; doesn't force one
  syncNow();
  const t = setInterval(syncNow, 90e3);
  const onVis = () => { if (document.visibilityState === 'hidden') syncNow(); };
  document.addEventListener('visibilitychange', onVis);
  return () => { clearInterval(t); clearTimeout(syncTimer); document.removeEventListener('visibilitychange', onVis); };
}
